import { type ViewerBreadcrumbRoom, viewerBreadcrumbRoom, viewerRoomPath } from '@polygraf/shared'
import { trpc } from '../trpc/client'

export interface SessionRoomContext {
	/** Link to the owning room, or null for an ad-hoc (roomless) session. */
	href: string | null
	/**
	 * The room as the breadcrumb needs it — space and room each addressable.
	 * Null for an ad-hoc session, and until the room query resolves.
	 */
	breadcrumbRoom: ViewerBreadcrumbRoom | null
}

/**
 * Resolves the room a live session belongs to, so a page can point back at it.
 *
 * A broadcast started from a room service lands on `/sessions/:id/present`, a
 * route it shares with ad-hoc Quick-service sessions. Without this the page
 * cannot tell the two apart and sends everyone to the Quick-services list
 * (MU feedback 2026-08-05).
 *
 * `sessions.get` carries only `roomId`, and the room URL needs the space too,
 * so the space is read from `rooms.get`. Pass a null `roomId` for an ad-hoc
 * session and the query stays disabled.
 */
export function useSessionRoomContext(roomId: string | null): SessionRoomContext {
	const roomQuery = trpc.rooms.get.useQuery({ id: roomId ?? '' }, { enabled: !!roomId })
	const room = roomQuery.data
	return {
		// The back arrow only needs the room to be addressable; it must keep
		// working for a room whose names have not resolved, because `href` is
		// also what tells the page this is a room broadcast, not an ad-hoc one.
		href: roomId && room?.spaceId ? viewerRoomPath(room.spaceId, roomId) : null,
		breadcrumbRoom: viewerBreadcrumbRoom(roomId ? { ...room, id: roomId } : null),
	}
}
